import Cookies from "js-cookie";
import { useCallback } from "react";

import { useAuth } from "~/contexts/AuthProvider";
import {
  AUTHORIZATIONS_STORAGE_KEY,
  AUTH_TOKEN_STORAGE_KEY,
  GLOBAL_SETTINGS_STORAGE_KEY,
} from "~/helpers/consts";
import { setWsCookie } from "~/helpers/setWsCookie";

export const AUTH_IMPERSONATED_BY_TOKEN_STORAGE_KEY = "impersonated_by_token";
export const IMPERSONATING_FLAG_STORAGE_KEY = "impersonated";
export const IMPERSONATION_TOKEN_COOKIE_KEY = "impersonation_token";

// We intentionally read the auth token directly from localStorage here.
// Impersonation runs at a low browser level (cookies, storage, hard redirects),
// where React state from AuthProvider may be stale or not yet initialized.
// localStorage is the only reliable source of truth at this point.

export function useImpersonation() {
  const { setToken } = useAuth();
  const impersonationToken = localStorage.getItem(
    AUTH_IMPERSONATED_BY_TOKEN_STORAGE_KEY,
  );
  const flag = localStorage.getItem(IMPERSONATING_FLAG_STORAGE_KEY);
  const isImpersonating = Boolean(flag);
  const canReturnToAccount = Boolean(impersonationToken);
  const currentAuthKey = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);

  const startImpersonation = useCallback(
    async (token: string) => {
      if (!token) return;

      const alreadySaved = !!impersonationToken;

      // if we're currently already logged in - save the token to be able to return to your account later
      // do not override if already exists (multiple impersonation)
      if (currentAuthKey && !alreadySaved) {
        localStorage.setItem(
          AUTH_IMPERSONATED_BY_TOKEN_STORAGE_KEY,
          currentAuthKey,
        );
      }

      localStorage.setItem(IMPERSONATING_FLAG_STORAGE_KEY, "1");
      setToken(token);

      // set WS-cookie for the new user before reloading so ActionCable
      // authenticates as the impersonated user, not the previous one
      await setWsCookie(token);
      window.location.href = "/";
    },
    [currentAuthKey, impersonationToken, setToken],
  );

  const returnToAccount = useCallback(async () => {
    if (!impersonationToken) return;

    setToken(impersonationToken);
    localStorage.removeItem(IMPERSONATING_FLAG_STORAGE_KEY);
    localStorage.removeItem(AUTH_IMPERSONATED_BY_TOKEN_STORAGE_KEY);

    // set WS-cookie for the original user before reloading so ActionCable
    // authenticates correctly after returning from impersonation
    await setWsCookie(impersonationToken);
    window.location.href = "/";
  }, [impersonationToken, setToken]);

  const consumeImpersonationCookie = useCallback(() => {
    const cookieToken = Cookies.get(IMPERSONATION_TOKEN_COOKIE_KEY);
    const currentAuthKey = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);

    if (!cookieToken) return;

    // skip invalid or duplicated values
    if (
      !cookieToken ||
      ["null", "undefined", "", currentAuthKey].includes(cookieToken)
    ) {
      Cookies.remove(IMPERSONATION_TOKEN_COOKIE_KEY);

      return;
    }

    // save localStorage keys that we want to preserve
    const preserveKeys = [
      AUTHORIZATIONS_STORAGE_KEY,
      AUTH_IMPERSONATED_BY_TOKEN_STORAGE_KEY,
      GLOBAL_SETTINGS_STORAGE_KEY,
    ];
    const preserved: Record<string, string> = {};
    for (const key of preserveKeys) {
      const val = localStorage.getItem(key);
      if (val !== null) preserved[key] = val;
    }

    // clean localStorage and restore saved keys
    localStorage.clear();
    for (const [key, value] of Object.entries(preserved)) {
      localStorage.setItem(key, value);
    }

    Cookies.remove(IMPERSONATION_TOKEN_COOKIE_KEY);

    startImpersonation(cookieToken);
  }, [startImpersonation]);

  const clearImpersonationState = useCallback(() => {
    localStorage.removeItem(IMPERSONATING_FLAG_STORAGE_KEY);
    localStorage.removeItem(AUTH_IMPERSONATED_BY_TOKEN_STORAGE_KEY);
    Cookies.remove(IMPERSONATION_TOKEN_COOKIE_KEY);
  }, []);

  return {
    isImpersonating,
    canReturnToAccount,
    returnToAccount,
    consumeImpersonationCookie,
    clearImpersonationState,
  };
}
